import {
  ISKELE_TIPI_ETIKET,
  ISKELE_TIPLERI,
  metrajOzeti,
  metrajYazi,
  olcuAciklama,
  olcuTipi,
  type IskeleTipi,
  type OlcuInput,
} from '@mod/domain';

/**
 * ÖLÇÜ LİSTESİ — keşif kartında boyutların DETAYLI gösterimi.
 *
 * ⚠️ Önceki hâli yalnız `5×5×1` yazıyordu: hangi sayı en, hangisi yük belli
 *    değildi ve sonuç hiç görünmüyordu. Şef "bu metraj nereden çıktı" sorusunu
 *    ekrandan cevaplayamıyordu — hatalı girilmiş bir ölçüyü fark etmenin yolu
 *    yoktu.
 *
 * Şimdi her satır üç şeyi birden söylüyor: TİP · hangi ölçüler · sonuç.
 * Hesap `@mod/domain`den geliyor; ekran kendi formülünü yazmıyor.
 */
export interface OlcuSatiri extends OlcuInput {
  ad?: string | null;
}

/**
 * API'den gelen iki toplamı insan diline çevirir: "120,00 m³ · 25,63 m²".
 *
 * Köprü fonksiyon: sunucu alanları `metrajM3/metrajM2`, domain fonksiyonu
 * `m3/m2` bekliyor. Her ekranda tek tek eşlemek yerine tek yerde.
 */
export function metrajGoster(j: { metrajM3?: number | null; metrajM2?: number | null }): string {
  return metrajYazi({ m3: j.metrajM3 ?? 0, m2: j.metrajM2 ?? 0 });
}

export function OlcuListesi({
  olculer,
  m3,
  m2,
  kucuk = false,
  manuel = false,
}: {
  olculer: readonly OlcuSatiri[];
  /** İşin kayıtlı toplamları — nihai onaylanmışsa o değerler. */
  m3?: number | null;
  m2?: number | null;
  /** Telefonda daha sıkışık dizilim. */
  kucuk?: boolean;
  /**
   * Toplam ELLE girildiyse işaretlenir.
   *
   * ⚠️ Bu rozet olmadan ekran yalan söylerdi: altta "10 × 10 × 10 = 1000 m³"
   *    yazan satırlar dururken üstte 850 m³ görünür ve kullanıcı ikisini
   *    bağdaştıramazdı. Rozet, toplamın satırlardan GELMEDİĞİNİ söyler.
   */
  manuel?: boolean;
}) {
  if (olculer.length === 0 && !manuel) return null;
  const toplamVar = (m3 ?? 0) > 0 || (m2 ?? 0) > 0;

  return (
    <div className={kucuk ? 'space-y-1' : 'space-y-1.5'}>
      {toplamVar && (
        <p className={kucuk ? 'text-sm font-medium' : 'font-medium text-ink'}>
          Toplam <span className="num">{metrajYazi({ m3: m3 ?? 0, m2: m2 ?? 0 })}</span>
          {manuel && (
            <span className="rozet ml-2 border-warn-line bg-warn-bg text-[10px] text-warn">
              elle girildi
            </span>
          )}
        </p>
      )}
      {manuel && olculer.length > 0 && (
        <p className={`text-warn ${kucuk ? 'text-[11px]' : 'text-xs'}`}>
          Aşağıdaki keşif satırları <strong>kayıtta kalır</strong> ama toplamı artık
          onlar belirlemiyor.
        </p>
      )}

      {olculer.map((o, i) => (
        <div
          key={i}
          className={`flex flex-wrap items-baseline gap-x-2 gap-y-0.5 rounded-(--radius-mod) bg-surface-2 px-2.5 py-1.5 ${
            kucuk ? 'text-xs' : 'text-sm'
          }`}
        >
          <span className="rozet border-navy-line bg-navy-bg text-[10px] text-navy">
            {ISKELE_TIPI_ETIKET[olcuTipi(o)]}
          </span>
          {o.ad && <span className="font-medium text-ink">{o.ad}</span>}
          <span className="num text-ink-2">{olcuAciklama(o)}</span>
        </div>
      ))}
    </div>
  );
}

/* ================================================================== */
/*  NİHAİ METRAJ SEÇİCİSİ — "keşif aynen" / "elle gir"                 */
/* ================================================================== */

/**
 * ELLE GİRİLEN ÖLÇÜ SATIRI.
 *
 * ⚠️ ALANLAR STRING, sayı DEĞİL. Kullanıcı "8," yazarken her tuşta sayıya
 *    çevirmek virgülü siliyor ve imleci zıplatıyordu.
 *
 * ⚠️ TOPLAM DEĞİL, BOYUT girilir (kullanıcı kararı 06.08.2026). Bir gün önce
 *    tek toplam sayı yazılıyordu; o hâli "hangi parçanın değiştiği" bilgisini
 *    yok ediyordu — 02.08'de aynı gerekçeyle kaldırılmış, 05.08'de geri
 *    getirilmiş, 06.08'de bu biçime oturmuştu. Şimdiki hâl ikisinin de
 *    istediğini veriyor: şef rakamı elle belirliyor AMA sistem neye
 *    dayandığını biliyor.
 */
export interface ManuelOlcu {
  tip: IskeleTipi;
  ad?: string | null;
  en: string;
  boy: string;
  yuk: string;
  yaricap: string;
}

/** "1.234,56" / "1234.56" → 1234.56. Boş ya da bozuksa 0. */
export function sayiyaCevir(s: string): number {
  const n = Number(String(s).replace(/\s/g, '').replace(',', '.'));
  return Number.isFinite(n) && n >= 0 ? n : 0;
}

const sayiYaz = (n: number | null | undefined): string =>
  n == null || n === 0 ? '' : String(n).replace('.', ',');

/** Kayıtlı ölçü satırını düzenlenebilir hâle çevirir — ŞABLON buradan gelir. */
export function manuelSatiraCevir(o: OlcuSatiri): ManuelOlcu {
  return {
    tip: olcuTipi(o),
    ad: o.ad ?? null,
    en: sayiYaz(o.en),
    boy: sayiYaz(o.boy),
    yuk: sayiYaz(o.yuk),
    yaricap: sayiYaz(o.yaricap),
  };
}

/**
 * Elle girilen satırı domain girdisine çevirir.
 *
 * ⚠️ TİPE GÖRE YALNIZ ANLAMLI ALANLAR gönderilir. `kapama` satırında en/boy
 *    yoktur (daire), `normal` dışında yükseklik anlamsızdır — hepsini birden
 *    göndermek sunucudaki `olcuSchema` doğrulamasına takılırdı.
 */
export function manuelOlcuyuCevir(m: ManuelOlcu): OlcuInput {
  if (m.tip === 'kapama') {
    return { tip: 'kapama', ad: m.ad ?? null, yaricap: sayiyaCevir(m.yaricap) };
  }
  if (m.tip === 'asma') {
    return { tip: 'asma', ad: m.ad ?? null, en: sayiyaCevir(m.en), boy: sayiyaCevir(m.boy) };
  }
  return {
    tip: 'normal',
    ad: m.ad ?? null,
    en: sayiyaCevir(m.en),
    boy: sayiyaCevir(m.boy),
    yuk: sayiyaCevir(m.yuk),
  };
}

/**
 * TESLİME GİDECEK ÖLÇÜ SATIRLARI — seçime göre.
 *
 * ⚠️ Tek kaynak. "Üstteki metraj gerçek zamanlı güncellensin" isteğinin
 *    karşılığı bu: ekranda gösterilen toplam da, sunucuya gidecek satırlar da
 *    aynı fonksiyondan çıkar. İkisi ayrı hesaplansaydı ekranda 850 görünüp
 *    sunucuya 1000 gitmesi mümkün olurdu — fark edilmesi en zor hata sınıfı.
 */
export function nihaiOlculer(
  olculer: readonly OlcuSatiri[],
  manuel: ManuelOlcu[] | null,
): OlcuInput[] {
  if (!manuel) return olculer as OlcuInput[];
  return manuel.map(manuelOlcuyuCevir);
}

/** Gösterilecek toplam — satırlardan hesaplanır, ayrı bir yol yoktur. */
export function nihaiToplam(
  olculer: readonly OlcuSatiri[],
  manuel: ManuelOlcu[] | null,
): { m3: number; m2: number } {
  const o = metrajOzeti(nihaiOlculer(olculer, manuel));
  return { m3: o.m3, m2: o.m2 };
}

/** Boş bir satır — hiç ölçüsü olmayan işte "elle gir" seçilirse. */
const bosSatir = (tip: IskeleTipi = 'normal'): ManuelOlcu => ({
  tip,
  ad: null,
  en: '',
  boy: '',
  yuk: '',
  yaricap: '',
});

/**
 * NİHAİ METRAJ SEÇİCİSİ.
 *
 * ⚠️ ELLE GİRİŞ = BOYUT GİRİŞİ, toplam girişi değil (kullanıcı kararı
 *    06.08.2026). Satırlar keşifteki ŞABLONLA açılır: hacimliyse üç kutu
 *    (en × boy × yük), asmaysa iki (en × boy), kapamaysa bir (yarıçap).
 *    Toplam her tuşta yeniden hesaplanır.
 *
 *    Kazancı çift taraflı: şef rakamı istediği gibi belirliyor AMA kayıt
 *    "bu sayı nereden çıktı" sorusunu cevaplamaya devam ediyor. Tek toplam
 *    yazdırmak bu bilgiyi yok ediyordu — 02.08'de tam bu gerekçeyle
 *    kaldırılmıştı.
 *
 * ⚠️ Keşif satırları SİLİNMEZ. "Elle gir" onların düzenlenebilir bir
 *    KOPYASINI açar; vazgeçilirse tek tıkla keşfe dönülür.
 *
 * ⚠️ İki ekran (masaüstü `IsDetay`, mobil `SahaIsDetay`) bu TEK bileşeni
 *    kullanır. Ayrı yazılsalardı biri m² alanını unutur ve karışık işlerde
 *    sessiz metraj kaybı olurdu — 02.08'de tam olarak bu yaşandı (c86e7bf).
 */
export function NihaiMetrajSecici({
  olculer,
  manuel,
  onDegis,
  kucuk = false,
}: {
  olculer: readonly OlcuSatiri[];
  /** `null` → keşif ölçüsü aynen. Dolu → satırlar elle düzenleniyor. */
  manuel: ManuelOlcu[] | null;
  onDegis: (m: ManuelOlcu[] | null) => void;
  kucuk?: boolean;
}) {
  const secili = manuel !== null;

  const secenek = (aktif: boolean) =>
    `flex flex-1 items-center justify-center gap-1.5 rounded-(--radius-mod) border px-3 py-2 text-sm font-medium ${
      aktif ? 'border-navy bg-navy text-white' : 'border-line-2 text-ink-2 hover:border-navy'
    }`;

  const kutu =
    'num w-20 rounded-(--radius-mod) border border-line-2 px-2 py-1.5 text-sm outline-none focus:border-navy';

  const satirDegis = (i: number, yama: Partial<ManuelOlcu>) => {
    if (!manuel) return;
    onDegis(manuel.map((s, j) => (j === i ? { ...s, ...yama } : s)));
  };

  return (
    <div className="rounded-(--radius-mod) border border-line bg-surface-2 p-3">
      <p className="mb-2 text-sm font-medium text-ink-2">Nihai metraj nasıl belirlensin?</p>

      <div className="flex gap-2">
        <button type="button" onClick={() => onDegis(null)} className={secenek(!secili)}>
          Keşif ölçüsüyle aynı
        </button>
        <button
          type="button"
          /*
           * Elle girişe geçerken satırlar KEŞİF DEĞERLERİYLE dolar, boş değil.
           * Şefin tipik işi "yüksekliği 10'dan 8,5'e çek" — sıfırdan yazdırmak
           * hem yavaş hem de bir alanı boş bırakıp metrajı düşürme riski.
           */
          onClick={() =>
            onDegis(
              olculer.length ? olculer.map(manuelSatiraCevir) : [bosSatir()],
            )
          }
          className={secenek(secili)}
        >
          Elle gir
        </button>
      </div>

      {secili && (
        <div className="mt-3 space-y-2">
          {manuel.map((s, i) => (
            <div
              key={i}
              className="flex flex-wrap items-end gap-2 rounded-(--radius-mod) bg-surface p-2"
            >
              <span className="rozet border-navy-line bg-navy-bg text-[10px] text-navy">
                {ISKELE_TIPI_ETIKET[s.tip]}
              </span>
              {s.ad && <span className="text-xs font-medium text-ink">{s.ad}</span>}

              {/* TİPE GÖRE ALAN: hacimli üç, asma iki, kapama bir. */}
              {s.tip === 'kapama' ? (
                <label className="block">
                  <span className="mb-0.5 block text-[11px] text-wait">Yarıçap (m)</span>
                  <input
                    className={kutu}
                    inputMode="decimal"
                    value={s.yaricap}
                    onChange={(e) => satirDegis(i, { yaricap: e.target.value })}
                  />
                </label>
              ) : (
                <>
                  <label className="block">
                    <span className="mb-0.5 block text-[11px] text-wait">En (m)</span>
                    <input
                      className={kutu}
                      inputMode="decimal"
                      value={s.en}
                      onChange={(e) => satirDegis(i, { en: e.target.value })}
                    />
                  </label>
                  <label className="block">
                    <span className="mb-0.5 block text-[11px] text-wait">Boy (m)</span>
                    <input
                      className={kutu}
                      inputMode="decimal"
                      value={s.boy}
                      onChange={(e) => satirDegis(i, { boy: e.target.value })}
                    />
                  </label>
                  {s.tip === 'normal' && (
                    <label className="block">
                      <span className="mb-0.5 block text-[11px] text-wait">Yük. (m)</span>
                      <input
                        className={kutu}
                        inputMode="decimal"
                        value={s.yuk}
                        onChange={(e) => satirDegis(i, { yuk: e.target.value })}
                      />
                    </label>
                  )}
                </>
              )}

              {/* Satırın kendi sonucu — "bu sayı nereden çıktı" ekranda kalsın. */}
              <span className="num ml-auto text-xs text-ink-2">
                = {olcuAciklama(manuelOlcuyuCevir(s)).split('= ')[1] ?? '—'}
              </span>

              {manuel.length > 1 && (
                <button
                  type="button"
                  title="Bu satırı çıkar"
                  onClick={() => onDegis(manuel.filter((_, j) => j !== i))}
                  className="rounded-(--radius-mod) px-2 py-1 text-xs text-muted hover:bg-crit-bg hover:text-crit"
                >
                  ✕
                </button>
              )}
            </div>
          ))}

          <div className="flex flex-wrap gap-2">
            {ISKELE_TIPLERI.map((t) => (
              <button
                key={t}
                type="button"
                onClick={() => onDegis([...manuel, bosSatir(t)])}
                className="rounded-(--radius-mod) border border-line-2 px-2.5 py-1 text-xs text-wait hover:border-navy hover:text-navy"
              >
                + {ISKELE_TIPI_ETIKET[t]}
              </button>
            ))}
          </div>

          <p className={`text-wait ${kucuk ? 'text-[11px]' : 'text-xs'}`}>
            Toplam yukarıda anında güncellenir. Keşif satırları kayıtta kalır.
          </p>
        </div>
      )}

      {!secili && (
        <p className="mt-2 text-xs text-wait">
          Toplam keşif satırlarından hesaplanır. Satırları kalıcı düzeltmek için{' '}
          <strong>⋮ → keşif</strong>.
        </p>
      )}
    </div>
  );
}
